import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, Alert, CircularProgress, TextField, Button, TablePagination } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import { serverServicesAPI } from '../services/api';
import { usePermissions } from '../context/PermissionsContext';
import ServiceActionsTable from '../components/ServiceActionsTable';

const NATIVE = { select: { native: true }, inputLabel: { shrink: true } };
const PER = 25;

// Every stop and start of a STARLIMS service made from the portal, newest first. Cloud Services administrators only.
function ServiceActions() {
  const { isAdmin, loading: permsLoading } = usePermissions();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customer, setCustomer] = useState('');
  const [host, setHost] = useState('');
  const [action, setAction] = useState('');
  const [status, setStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await serverServicesAPI.audit({ customer_code: customer.trim() || undefined, host: host.trim() || undefined, action: action || undefined, status: status || undefined, limit: PER, offset: page * PER });
      setRows(res.data.items); setTotal(res.data.total);
    } catch (err) { setRows([]); setError(err.response?.data?.detail || 'Could not load the audit trail'); }
    finally { setLoading(false); }
  }, [customer, host, action, status, page]);

  useEffect(() => { if (!permsLoading && isAdmin()) load(); }, [load, permsLoading]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(0); }, [customer, host, action, status]);

  if (permsLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (!isAdmin()) return <Alert severity="info">Only Cloud Services administrators can see the service audit trail.</Alert>;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Service Actions</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every stop and start of a STARLIMS service made from Server Resources: who, when, why and what the server reported. Records cannot be changed or deleted.
      </Typography>
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" label="Customer code" value={customer} onChange={(e) => setCustomer(e.target.value)} sx={{ width: 140 }} />
        <TextField size="small" label="Server" value={host} onChange={(e) => setHost(e.target.value)} sx={{ width: 200 }} />
        <TextField select size="small" label="Action" value={action} onChange={(e) => setAction(e.target.value)} slotProps={NATIVE} sx={{ width: 130 }}>
          <option value="">All</option><option value="stop">Stop</option><option value="start">Start</option>
        </TextField>
        <TextField select size="small" label="Result" value={status} onChange={(e) => setStatus(e.target.value)} slotProps={NATIVE} sx={{ width: 150 }}>
          <option value="">All</option><option value="succeeded">Succeeded</option><option value="failed">Failed</option><option value="running">Running</option>
        </TextField>
        <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
      </Paper>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
        {!loading && rows.length === 0 && !error && <Typography sx={{ p: 3 }} color="text.secondary">No stops or starts match.</Typography>}
        {!loading && rows.length > 0 && <ServiceActionsTable rows={rows} />}
        <TablePagination component="div" count={total} page={page} rowsPerPage={PER} rowsPerPageOptions={[PER]} onPageChange={(_, p) => setPage(p)} />
      </Paper>
    </Box>
  );
}

export default ServiceActions;
